import React, { useState, useEffect, useCallback } from 'react';

const DISMISS_KEY = 'pwa-install-dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

function isIOSSafari() {
  const ua = window.navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function wasDismissedRecently() {
  try {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (!dismissed) return false;
    return Date.now() - Number(dismissed) < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIOSSafari()) {
      const timer = setTimeout(() => {
        setShowIOSPrompt(true);
        setVisible(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    setShowIOSPrompt(false);
    setDeferredPrompt(null);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* ignore */ }
  }, []);

  if (!visible) return null;

  if (showIOSPrompt) {
    return (
      <div className="lh-pwa-prompt fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-slide-up">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-dark-100 overflow-hidden">
          <div className="flex items-start gap-3 p-4">
            <img
              src="/lat-icon.png"
              alt="LAT"
              className="w-12 h-12 rounded-xl flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-dark-900 text-sm">Install LAT Learning</p>
              <p className="text-dark-500 text-xs mt-1 leading-relaxed">
                Tap the{' '}
                <span className="inline-flex items-center">
                  <svg className="w-4 h-4 text-primary-500 mx-0.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/>
                  </svg>
                </span>
                {' '}Share button, then <strong>"Add to Home Screen"</strong> for the best experience.
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-dark-400 hover:text-dark-600 p-1 flex-shrink-0"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (deferredPrompt) {
    return (
      <div className="lh-pwa-prompt fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-slide-up">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-dark-100 overflow-hidden">
          <div className="flex items-center gap-3 p-4">
            <img
              src="/lat-icon.png"
              alt="LAT"
              className="w-12 h-12 rounded-xl flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-dark-900 text-sm">Install LAT Learning</p>
              <p className="text-dark-500 text-xs mt-0.5">Learn on the go — quick access from your home screen.</p>
            </div>
          </div>
          <div className="flex border-t border-dark-100">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 text-sm font-medium text-dark-500 hover:bg-dark-50 transition-colors"
            >
              Not now
            </button>
            <div className="w-px bg-dark-100" />
            <button
              onClick={handleInstall}
              className="flex-1 py-3 text-sm font-semibold text-primary-500 hover:bg-primary-50 transition-colors"
            >
              Install
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallPrompt;
